import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { showToast } from '../components/Common/Toast';
import BrandedLogo from '../components/Common/BrandedLogo';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/set-password`,
      });

      if (resetError) {
        throw resetError;
      }

      setEmailSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <BrandedLogo className="mx-auto h-12 w-auto" />
        {/* Board #1971. Signup opened at <h1> while Login, Forgot password and Set password all
            opened at <h2>, so three of the four auth pages had NO <h1> at all and their document
            outline started one level down. Every visual class is unchanged - this is the outline a
            screen reader and the browser's own heading navigation read, not the type size. */}
        <h1 className="mt-6 text-center text-3xl font-bold text-white">
          Reset your password
        </h1>
        <p className="mt-2 text-center text-sm text-gray-400">
          Enter your email and we'll send you a reset link
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="py-8 px-4 sm:px-10 rounded-2xl backdrop-blur-xl border border-white/10 shadow-2xl" style={{
          background: "rgba(30, 41, 59, 0.7)",
          backdropFilter: "blur(15px)"
        }}>
          {emailSent ? (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="mt-4 text-lg font-medium text-white">
                Check your email
              </h2>
              <p className="mt-2 text-sm text-gray-400">
                We sent a password reset link to <span className="text-white font-medium">{email}</span>
              </p>
              <p className="mt-4 text-sm text-gray-500">
                Didn't receive the email? Check your spam folder, or{' '}
                <button
                  type="button"
                  className="text-blue-400 hover:text-blue-300 font-medium"
                  onClick={() => setEmailSent(false)}
                >
                  try again
                </button>
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-6 w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to login
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 rounded-md bg-red-500/20 border border-red-500/50 text-red-200 text-sm">
                  {error}
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-200">
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-md placeholder-gray-500 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {loading ? 'Sending...' : 'Send reset link'}
                  </button>
                </div>
              </form>

              <div className="mt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-400">
                    Remember your password?{' '}
                    <button
                      type="button"
                      className="font-medium text-blue-400 hover:text-blue-300 bg-transparent border-none cursor-pointer"
                      onClick={() => navigate('/login')}
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
